import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Video, Building2, Check, ArrowRight, ArrowLeft, Calendar as CalIcon, Loader2, Copy } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const LAWYERS = [
  "Avv. Marco Valente",
  "Avv. Giulia Ferri",
  "Avv. Luca Bianchi",
  "Avv. Paolo Greco",
  "Primo disponibile",
];

export default function BookingSection({ trigger }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [config, setConfig] = useState({ sedi: [], areas: [], time_slots: [] });
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const [form, setForm] = useState({
    mode: "", sede: "", area: "", lawyer: "Primo disponibile",
    date: "", time: "", client_name: "", client_email: "", client_phone: "", note: "",
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (open) {
      api.get("/public/config").then((r) => setConfig(r.data)).catch(() => {});
    }
  }, [open]);

  useEffect(() => {
    if (form.date && step === 3) {
      setLoadingSlots(true);
      api.get(`/public/availability`, { params: { date: form.date, mode: form.mode } })
        .then((r) => setSlots(r.data.slots))
        .catch(() => setSlots([]))
        .finally(() => setLoadingSlots(false));
    }
  }, [form.date, form.mode, step]);

  const reset = () => {
    setStep(1);
    setResult(null);
    setForm({ mode: "", sede: "", area: "", lawyer: "Primo disponibile", date: "", time: "", client_name: "", client_email: "", client_phone: "", note: "" });
  };

  const handleOpenChange = (v) => {
    setOpen(v);
    if (!v) setTimeout(reset, 300);
  };

  const canNext = () => {
    if (step === 1) return form.mode && (form.mode === "online" || form.sede);
    if (step === 2) return form.area && form.lawyer;
    if (step === 3) return form.date && form.time;
    return true;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        client_name: form.client_name, client_email: form.client_email,
        client_phone: form.client_phone, mode: form.mode,
        sede: form.mode === "in_person" ? form.sede : null,
        area: form.area, lawyer: form.lawyer, date: form.date, time: form.time,
        note: form.note,
      };
      const res = await api.post("/public/appointments", payload);
      setResult(res.data);
      setStep(6);
      toast.success("Richiesta inviata! Attendi la conferma dell'avvocato.");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Errore durante la prenotazione";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const today = new Date().toISOString().split("T")[0];

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="bg-[#FFFFFF] border border-amber-500/20 text-stone-900 max-w-lg max-h-[92vh] overflow-y-auto" data-testid="booking-modal">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-stone-900">
            {step === 6 ? "Richiesta Inviata" : "Prenota una Consulenza"}
          </DialogTitle>
        </DialogHeader>

        {step !== 6 && (
          <div className="flex gap-1.5 mb-2" data-testid="booking-steps">
            {[1, 2, 3, 4].map((s) => (
              <div key={s} className={`h-1 flex-1 rounded-full transition-colors ${s <= step ? "bg-amber-500" : "bg-stone-200"}`} />
            ))}
          </div>
        )}

        {/* Step 1: Mode */}
        {step === 1 && (
          <div className="space-y-4 animate-fade-up">
            <p className="text-sm text-stone-500">Scegli la modalita della consulenza.</p>
            <div className="grid grid-cols-2 gap-3">
              <button data-testid="booking-type-online" onClick={() => set("mode", "online")}
                className={`p-5 rounded-xl border text-left transition-all ${form.mode === "online" ? "border-amber-500 bg-amber-500/10" : "border-stone-300 hover:border-amber-500/50"}`}>
                <Video className="w-6 h-6 text-amber-600 mb-2" />
                <div className="font-semibold text-stone-900">Online</div>
                <div className="text-xs text-stone-500">Videoconferenza con link automatico</div>
              </button>
              <button data-testid="booking-type-in-person" onClick={() => set("mode", "in_person")}
                className={`p-5 rounded-xl border text-left transition-all ${form.mode === "in_person" ? "border-amber-500 bg-amber-500/10" : "border-stone-300 hover:border-amber-500/50"}`}>
                <Building2 className="w-6 h-6 text-amber-600 mb-2" />
                <div className="font-semibold text-stone-900">In Studio</div>
                <div className="text-xs text-stone-500">Presso le nostre sedi</div>
              </button>
            </div>
            {form.mode === "in_person" && (
              <div className="space-y-2 animate-fade-up">
                <Label className="text-stone-600 text-sm">Seleziona la sede</Label>
                <div className="grid grid-cols-2 gap-2">
                  {config.sedi.map((s) => (
                    <button key={s} data-testid={`booking-sede-${s.replace(/\s+/g, "-").toLowerCase()}`} onClick={() => set("sede", s)}
                      className={`p-3 rounded-lg border text-sm transition-all ${form.sede === s ? "border-amber-500 bg-amber-500/10 text-stone-900" : "border-stone-300 text-stone-500 hover:border-amber-500/50"}`}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 2: Area + lawyer */}
        {step === 2 && (
          <div className="space-y-4 animate-fade-up">
            <div className="space-y-2">
              <Label className="text-stone-600 text-sm">Area legale</Label>
              <div className="grid gap-2">
                {config.areas.map((a) => (
                  <button key={a} data-testid={`booking-area-${a.slice(0, 8).replace(/\s+/g, "-").toLowerCase()}`} onClick={() => set("area", a)}
                    className={`p-3 rounded-lg border text-sm text-left transition-all ${form.area === a ? "border-amber-500 bg-amber-500/10 text-stone-900" : "border-stone-300 text-stone-600 hover:border-amber-500/50"}`}>
                    {a}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-stone-600 text-sm">Avvocato di riferimento</Label>
              <div className="flex flex-wrap gap-2">
                {LAWYERS.map((l) => (
                  <button key={l} onClick={() => set("lawyer", l)}
                    className={`px-3 py-2 rounded-full border text-xs transition-all ${form.lawyer === l ? "border-amber-500 bg-amber-500/10 text-stone-900" : "border-stone-300 text-stone-500 hover:border-amber-500/50"}`}>
                    {l}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Date + slot */}
        {step === 3 && (
          <div className="space-y-4 animate-fade-up">
            <div className="space-y-2">
              <Label className="text-stone-600 text-sm flex items-center gap-2"><CalIcon className="w-4 h-4 text-amber-600" /> Data</Label>
              <Input type="date" min={today} value={form.date} data-testid="booking-date-picker"
                onChange={(e) => { set("date", e.target.value); set("time", ""); }}
                className="bg-[#F4EFE4] border-stone-300 text-stone-900 [color-scheme:light]" />
            </div>
            {form.date && (
              <div className="space-y-2">
                <Label className="text-stone-600 text-sm">Fascia oraria (fuso Europe/Rome)</Label>
                {loadingSlots ? (
                  <div className="py-6 flex justify-center"><Loader2 className="w-5 h-5 text-amber-600 animate-spin" /></div>
                ) : (
                  <div className="grid grid-cols-3 gap-2">
                    {slots.map((s) => (
                      <button key={s.time} disabled={!s.available} data-testid={`booking-time-slot-${s.time.replace(":", "")}`}
                        onClick={() => set("time", s.time)}
                        className={`py-2 rounded-lg border text-sm transition-all disabled:opacity-30 disabled:cursor-not-allowed ${form.time === s.time ? "border-amber-500 bg-amber-500/10 text-stone-900" : "border-stone-300 text-stone-600 hover:border-amber-500/50"}`}>
                        {s.time}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Step 4: Client data */}
        {step === 4 && (
          <div className="space-y-3 animate-fade-up">
            <div className="space-y-1.5">
              <Label className="text-stone-600 text-sm">Nome e cognome *</Label>
              <Input value={form.client_name} data-testid="booking-name-input" onChange={(e) => set("client_name", e.target.value)}
                className="bg-[#F4EFE4] border-stone-300 text-stone-900" placeholder="Mario Rossi" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-stone-600 text-sm">Email *</Label>
              <Input type="email" value={form.client_email} data-testid="booking-email-input" onChange={(e) => set("client_email", e.target.value)}
                className="bg-[#F4EFE4] border-stone-300 text-stone-900" placeholder="mario.rossi@email.it" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-stone-600 text-sm">Telefono</Label>
              <Input value={form.client_phone} data-testid="booking-phone-input" onChange={(e) => set("client_phone", e.target.value)}
                className="bg-[#F4EFE4] border-stone-300 text-stone-900" placeholder="+39 ..." />
            </div>
            <div className="space-y-1.5">
              <Label className="text-stone-600 text-sm">Note sulla causa</Label>
              <Textarea value={form.note} data-testid="booking-note-input" onChange={(e) => set("note", e.target.value)}
                className="bg-[#F4EFE4] border-stone-300 text-stone-900 resize-none" rows={3} placeholder="Breve descrizione..." />
            </div>
            <div className="rounded-lg bg-[#F4EFE4] border border-stone-200 p-3 text-xs text-stone-500 space-y-1">
              <div><span className="text-amber-600/80">Modalita:</span> {form.mode === "online" ? "Online" : `In studio - ${form.sede}`}</div>
              <div><span className="text-amber-600/80">Area:</span> {form.area}</div>
              <div><span className="text-amber-600/80">Data:</span> {form.date} ore {form.time}</div>
            </div>
          </div>
        )}

        {/* Step 6: Confirmation */}
        {step === 6 && result && (
          <div className="space-y-4 text-center animate-fade-up py-2">
            <div className="w-16 h-16 rounded-full bg-amber-500/15 border border-amber-500/40 flex items-center justify-center mx-auto">
              <Check className="w-8 h-8 text-amber-600" />
            </div>
            <p className="text-stone-600 text-sm">
              Abbiamo ricevuto la tua richiesta per <strong className="text-stone-900">{result.date}</strong> alle <strong className="text-stone-900">{result.time}</strong>.
              La prenotazione è <strong className="text-stone-900">in attesa di conferma</strong> da parte dell'avvocato: riceverai una email di conferma all'indirizzo <strong className="text-amber-700">{result.client_email}</strong> con tutti i dettagli{result.mode === "online" ? ", incluso il link per la videoconferenza" : ""}.
            </p>
            <Button onClick={() => handleOpenChange(false)} data-testid="booking-done-btn"
              className="w-full bg-amber-500 hover:bg-amber-400 text-[#221C12] font-semibold">Chiudi</Button>
          </div>
        )}

        {/* Nav buttons */}
        {step !== 6 && (
          <div className="flex justify-between gap-3 pt-2">
            {step > 1 ? (
              <Button variant="ghost" onClick={() => setStep(step - 1)} data-testid="booking-back-btn"
                className="text-stone-500 hover:text-stone-900 hover:bg-stone-100">
                <ArrowLeft className="w-4 h-4 mr-1" /> Indietro
              </Button>
            ) : <span />}
            {step < 4 ? (
              <Button onClick={() => setStep(step + 1)} disabled={!canNext()} data-testid="booking-next-btn"
                className="bg-amber-500 hover:bg-amber-400 text-[#221C12] font-semibold disabled:opacity-40">
                Avanti <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button onClick={submit} disabled={submitting || !form.client_name || !form.client_email} data-testid="booking-submit-btn"
                className="bg-amber-500 hover:bg-amber-400 text-[#221C12] font-semibold disabled:opacity-40">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Conferma Prenotazione"}
              </Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
